<script setup lang="ts">
import { $getSelection, $isRangeSelection, $isTextNode } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

import { colorsByTheme } from "~/constants/style";
import { usePageStore } from "~/stores";
import { BACKGROUND_ALPHA } from "~/utils/color";

const props = defineProps<{
  color: string;
  isBackground: boolean;
}>();

const emit = defineEmits<{
  click: [color: string, isBackground: boolean];
}>();

const pageStore = usePageStore();

const editor = useLexicalComposer();

const currentStyle = ref("");

const colors = computed(() => colorsByTheme[pageStore.theme]);
const isDefaultColor = computed(() => !props.color);

const isSelected = computed(() => {
  const property = props.isBackground ? "background-color" : "color";
  const styleValue = currentStyle.value
    .split(";")
    .find((e) => e.trim().startsWith(`${property}:`))
    ?.split(":")[1]
    ?.trim();
  if (!styleValue) {
    return isDefaultColor.value;
  }
  if (props.isBackground) {
    return styleValue === `${props.color}${BACKGROUND_ALPHA}`;
  }
  return styleValue === props.color;
});

let unregisterListener: (() => void) | null = null;

onMounted(() => {
  const update = () =>
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = selection.anchor.getNode();
        if ($isTextNode(node)) {
          currentStyle.value = node.getStyle() || "";
        }
      }
    });
  update();
  unregisterListener = editor.registerUpdateListener(update);
});

onUnmounted(() => {
  if (unregisterListener) {
    unregisterListener();
  }
});
</script>

<template>
  <button
    type="button"
    class="group/color-item relative flex size-[26px] items-center justify-center truncate rounded border transition-all"
    :class="[isSelected ? 'border-[3px]' : 'border', isBackground && 'hover:scale-105']"
    :style="{
      backgroundColor: isBackground ? `${color}${BACKGROUND_ALPHA}` : 'transparent',
      borderColor: isDefaultColor ? colors.borderMd : color,
    }"
    :title="isDefaultColor ? 'Reset' : color"
    @click="emit('click', color, isBackground)">
    <div
      v-if="isBackground && !isDefaultColor"
      class="absolute inset-0 rounded opacity-0 transition-opacity group-hover/color-item:opacity-100" />
    <div
      v-if="!isBackground && !isDefaultColor"
      class="absolute inset-0 opacity-0 transition-opacity group-hover/color-item:opacity-100"
      :style="{ backgroundColor: `${color}${BACKGROUND_ALPHA}` }" />
    <div v-if="isDefaultColor" class="absolute h-0.5 -rotate-45 bg-red-500 px-4" />
    <span v-if="!isBackground" class="text-lg font-bold" :style="{ color: isDefaultColor ? colors.textMd : color }">
      A
    </span>
  </button>
</template>
